import React from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { useLibrary } from './hooks/useLibrary';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './scss/Library.module.scss';
import { Box, Grid, IconButton, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import LibraryList from './components/library-list/LibraryList';
import OwnLibraryIcon from '../../../assets/svg/own-library-icon.svg?react';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { selectAllDocumentTemplates } from '../../slices/document-templates/documentTemplatesSlice';
import ButtonDropDown from '../../common/button-drop-down/ButtonDropDown';
import MessageModal from '../../common/message-modal/MessageModal';
import DisclaimerText from '../../common/disclaimer-text/DisclaimerText';
import { acceptDisclaimer } from '../../slices/disclaimer/disclaimerSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CardViewContent } from './components/card-view-content/CardViewContent';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import EmploymentInfoText from './components/employment-info-text/EmploymentInfoText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { RequirementFilter } from './components/library-list/RequirementFilter';
import { isRequirementFilterAvailable, useLibraryQuery } from './hooks/useLibraryQuery';

/** Same breakpoint as `breakpoints.$tablet-mobile` in scss/settings/_breakpoints.scss. */
const TABLET_MOBILE_QUERY = '(max-width: 991px)';

const Library = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    libraryItems,
    handleTabChange,
    activeTab,
    pendingStatus: libraryItemsPendingStatus,
    setLibraryType,
    libraryType,
    region,
    setRegion,
    disclaimerMessage,
    setDisclaimerMessage,
    cardView,
    setCardView,
    setWorkflow,
    workflow,
    searchCleared,
    setSearchCleared,
    resetSearchFilter,
    employmentDataMessage,
    setEmploymentDataMessage,
    setSearchStringInputValue,
    searchStringInputValue,
  } = useLibrary();
  const { employeeDocRequirements, updateLibraryQuery } = useLibraryQuery();
  const isTabletMobile = useMediaQuery(TABLET_MOBILE_QUERY);
  const documentTemplates = useAppSelector(selectAllDocumentTemplates);
  const employeesLoading = useAppSelector((state) => state.employees.status);
  const customersLoading = useAppSelector((state) => state.customers.status);
  const suppliersLoading = useAppSelector((state) => state.suppliers.status);
  const disclaimerStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const disclaimerInfoLoadingStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const documentTemplatesLoading = useAppSelector((state) => state.documentTemplates.status);
  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };
  const regionsOptions = [
    { label: t('regions.au'), value: 'au' },
    { label: t('regions.uk'), value: 'uk' },
    { label: t('regions.global'), value: 'global' },
  ];
  const isCardViewShown = cardView && libraryType === 'businessKitz';
  const searchDisabled = libraryType === 'own';

  // Action bar right above the table: search, card/list view switcher and Document Terms.
  // In the list view it shares one rounded block with the table (see `.library_block_joined`).
  const renderToolbar = () => (
    <Box id='libraryToolbar' className={styles.toolbar}>
      <TextField
        id='librarySearchInput'
        className={styles.toolbar_search}
        placeholder={t('labels.searchByName')}
        slotProps={{
          htmlInput: { 'aria-label': t('labels.searchByName') },
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon
                  id='librarySearchInputSearchIcon'
                  className={styles.toolbar_search_icon}
                />
              </InputAdornment>
            ),
            endAdornment:
              searchStringInputValue.trim() && !searchDisabled ? (
                <InputAdornment position='end'>
                  <IconButton
                    id='libraryResetSearchFilterIconBtn'
                    size='small'
                    aria-label={t('buttons.close')}
                    onClick={() => resetSearchFilter()}
                  >
                    <CloseIcon id='libraryResetSearchFilterIconBtnCloseIcon' fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
          },
        }}
        value={searchStringInputValue}
        onChange={(e) => {
          setSearchStringInputValue(e.target.value);
          if (e.target.value.trim().length > 0) {
            setSearchCleared(false);
          }
        }}
        disabled={searchDisabled}
        onKeyUp={(e) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (e.target.value.trim() === '' && !searchCleared) {
            resetSearchFilter();
          }
        }}
      />
      {libraryType === 'businessKitz' && (
        <Box
          className={styles.card_list_view_container}
          role='group'
          aria-label={t('labels.libraryView')}
        >
          <Tooltip title={t('buttons.card')} placement='top' arrow>
            <button
              id='libraryCardBtn'
              type='button'
              aria-pressed={cardView}
              className={`${styles.variant_btn}${cardView ? ` ${styles.variant_btn_active}` : ''}`}
              onClick={() => setCardView(true)}
            >
              <DashboardIcon id='libraryCardBtnDashboardIcon' />
              <span id='libraryCardBtnText' className={styles.visually_hidden}>
                {t('buttons.card')}
              </span>
            </button>
          </Tooltip>
          <Tooltip title={t('buttons.list')} placement='top' arrow>
            <button
              id='libraryListBtn'
              type='button'
              aria-pressed={!cardView}
              onClick={() => {
                // Also drops the selected workflow (one history entry)
                setCardView(false);
              }}
              className={`${styles.variant_btn}${!cardView ? ` ${styles.variant_btn_active}` : ''}`}
            >
              <BallotIcon id='libraryListBtnBallotIcon' />
              <span id='libraryListBtnText' className={styles.visually_hidden}>
                {t('buttons.list')}
              </span>
            </button>
          </Tooltip>
        </Box>
      )}
      <Tooltip title={t('buttons.documentTerms')} placement='top' arrow>
        <IconButton
          id='libraryDocumentTermsBtn'
          aria-label={t('buttons.documentTerms')}
          className={styles.toolbar_icon_btn}
          onClick={() => {
            setDisclaimerMessage(true);
          }}
        >
          <InfoOutlinedIcon id='libraryDocumentTermsBtnInfoIcon' fontSize='small' />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Requirement filter: same availability rule as before (Business Kitz library, Employment
  // category, list view) and the same URL-backed value (?requirement=...).
  const showRequirementFilter = isRequirementFilterAvailable(libraryType, activeTab, cardView);

  const renderLibraryTypeButton = (
    type: 'businessKitz' | 'own',
    id: string,
    icon: React.ReactNode,
    label: string
  ) => {
    const active = libraryType === type;
    return (
      <button
        id={id}
        type='button'
        aria-pressed={active}
        className={`${styles.aside_nav_item}${active ? ` ${styles.aside_nav_item_active}` : ''}`}
        onClick={() => {
          // Resets the category / workflow / view / search in the URL (one history entry)
          setLibraryType(type);
        }}
      >
        {icon}
        <span className={styles.aside_nav_item_text}>{label}</span>
      </button>
    );
  };

  const renderAside = () => (
    <Box component='aside' id='libraryAside' className={styles.aside}>
      <Box className={styles.aside_libraries} role='group' aria-label={t('labels.libraryView')}>
        {renderLibraryTypeButton(
          'businessKitz',
          'libraryBusinessKitzLibraryBtn',
          <AccountBalanceOutlinedIcon
            id='libraryBusinessKitzLibraryBtnAccountBalanceIcon'
            className={styles.aside_nav_icon}
          />,
          t('buttons.businessKitzLibrary')
        )}
        {renderLibraryTypeButton(
          'own',
          'libraryOwnLibraryBtn',
          <OwnLibraryIcon
            id='libraryOwnLibraryBtnOwnLibraryIcon'
            className={styles.aside_nav_icon}
          />,
          t('buttons.ownLibrary')
        )}
      </Box>
      <Box className={styles.aside_divider} />
      <Box className={styles.aside_section}>
        <Tabs
          orientation={isTabletMobile ? 'horizontal' : 'vertical'}
          variant='scrollable'
          scrollButtons={isTabletMobile ? 'auto' : false}
          allowScrollButtonsMobile
          value={activeTab}
          onChange={(_, newValue) => handleTabChange(newValue)}
          aria-labelledby='libraryCategoryLabel'
          className={styles.category_tabs}
        >
          <Tab id='libraryAllTab' label='All' value='All' />
          <Tab id='libraryBusinessTab' label='Business' value='Business' />
          <Tab id='libraryEmploymentTab' label='Employment' value='Employment' />
          <Tab id='libraryCustomersTab' label='Customers' value='Customers' />
          <Tab id='librarySuppliersTab' label='Suppliers' value='Suppliers' />
        </Tabs>
      </Box>
      <Box className={styles.aside_bottom}>
        <Box className={`${styles.aside_section} ${styles.aside_country}`}>
          <Typography id='libraryCountryLabel' component='h2' className={styles.aside_label}>
            {t('labels.country')}
          </Typography>
          <Box className={`region-select-box ${styles.aside_select}`}>
            <ButtonDropDown
              id='libraryRegionDropdown'
              value={region}
              onChange={(event) => setRegion(event.target.value as 'uk' | 'au' | 'global')}
              options={regionsOptions}
              disabled={libraryType === 'own'}
              ariaLabel={t('labels.country')}
            />
          </Box>
        </Box>
        {showRequirementFilter && (
          <Box id='libraryRequirementFilterBox' className={styles.aside_filter}>
            <RequirementFilter
              value={employeeDocRequirements}
              onApply={(value) => updateLibraryQuery({ employeeDocRequirements: value })}
            />
          </Box>
        )}
      </Box>
      {/* Library overview, as on the Document Vault page. */}
      <PreviewTooltip title={<Preview />} arrow placement='right-end'>
        <button id='libraryOverviewInfoIcon' type='button' className={styles.aside_help}>
          <InfoOutlinedIcon fontSize='inherit' className={styles.aside_help_icon} />
          {t('labels.howLibraryWorks')}
        </button>
      </PreviewTooltip>
    </Box>
  );

  return (
    <>
      <AppHeader title={t('headers.documentTemplates')} />
      <Grid component='div' container spacing={3} sx={{ marginTop: '16px' }}>
        <Grid className={'controls-grid'} component='div' size={{ xs: 12 }}>
          <Box className={styles.page_description}>{t('messages.libraryDescription')}</Box>
          {/* Desktop: the aside (libraries, categories, country, requirement filter) sits to the
              left of the action bar + table / cards block. Tablet / mobile: it goes above it. */}
          <Box id='libraryLayout' className={styles.library_layout}>
            {renderAside()}
            <Box
              id='libraryBlock'
              className={`${styles.library_block}${
                isCardViewShown ? '' : ` ${styles.library_block_joined}`
              }`}
            >
              {renderToolbar()}
              <Box className={styles.library_block_content}>
                {isCardViewShown ? (
                  <CardViewContent
                    activeTab={activeTab}
                    changeTabFunction={handleTabChange}
                    templates={libraryItems}
                    setWorkflow={setWorkflow}
                    workflow={workflow}
                  />
                ) : (
                  <LibraryList
                    libraryItems={
                      libraryType === 'own'
                        ? [...documentTemplates].sort((a, b) => sortByDate(a, b))
                        : libraryItems
                    }
                    ownTemplate={libraryType === 'own'}
                    category={activeTab}
                    embedded
                  />
                )}
                {(libraryItemsPendingStatus ||
                  disclaimerInfoLoadingStatus === 'loading' ||
                  documentTemplatesLoading === 'loading' ||
                  employeesLoading === 'loading' ||
                  customersLoading === 'loading' ||
                  suppliersLoading === 'loading') && <ProgressOverlay position='absolute' />}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      {employmentDataMessage && (
        <MessageModal
          open={employmentDataMessage}
          modalTitle={t('employmentTemplatesInfo.employmentDocumentGuidance')}
          modalContent={<EmploymentInfoText />}
          headerVariant={'h5'}
          goBackButtonText={t('buttons.close')}
          noActionButton={true}
          onClose={() => {
            setEmploymentDataMessage(false);
          }}
        />
      )}
      {disclaimerMessage && (
        <MessageModal
          open={disclaimerMessage}
          modalTitle={t('headers.documentTerms')}
          modalContent={<DisclaimerText />}
          actionButtonText={t('buttons.accept')}
          headerVariant={'h5'}
          actionButtonFn={() => {
            dispatch(acceptDisclaimer())
              .unwrap()
              .finally(() => {
                setDisclaimerMessage(false);
              });
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDisclaimerMessage(false);
          }}
        />
      )}
      {disclaimerStatus === 'loadingAccepting' && <ProgressOverlay />}
    </>
  );
};

export default Library;
